import { createElement, Fragment, type ReactNode } from "react";

/**
 * Mehrsprachigkeit des Portals. Der deutsche Text ist der Schlüssel (`t("Aufträge")`) – der Code bleibt lesbar,
 * Deutsch braucht keinen Katalog. Übersetzungen liegen in `src/locales/<sprache>.json` und werden nur für die
 * gewählte Sprache geladen. Sprachwechsel lädt die Seite neu – dadurch ist `t()` eine einfache Funktion ohne Context.
 *
 * Regeln für neue Texte:
 * - sichtbare Texte immer über `t()` / `tn()` / `<T>`; feste Listen (Status, Labels) mit `msg()` markieren und beim Anzeigen `t()`
 * - Platzhalter `{name}` statt zusammengesetzter Sätze: `t("{n} Aufträge zurückgehalten", { n })`
 * - `pnpm i18n:check` (läuft in der CI) meldet fehlende englische Übersetzungen
 */

export type Locale = "de" | "en";
export const LOCALES: { id: Locale; label: string }[] = [
  { id: "de", label: "Deutsch" },
  { id: "en", label: "English" },
];
const STORAGE_KEY = "lc.locale";

let locale: Locale = "de";
let catalog: Record<string, string> = {};
/** Servertexte (Muster mit {0} …), siehe server-text.ts */
let serverCatalog: Record<string, string> = {};
export const getServerCatalog = () => serverCatalog;

export const getLocale = () => locale;
/** BCP-47-Locale für Intl (Datum, Zahlen, Währung). Englisch mit Tag vor Monat und Euro – passend für EU-Kunden. */
export const intlLocale = () => (locale === "en" ? "en-GB" : "de-DE");

const isLocale = (x: unknown): x is Locale => x === "de" || x === "en";

function stored(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

/** vor dem ersten Rendern: gespeicherte Wahl, sonst Browsersprache (Deutsch, wenn der Browser Deutsch kennt) */
export async function initI18n(): Promise<void> {
  const langs = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];
  locale = stored() ?? (langs.some((l) => l?.toLowerCase().startsWith("de")) || !langs.length ? "de" : "en");
  if (locale === "en") {
    try {
      const [ui, server] = await Promise.all([import("../locales/en.json"), import("../locales/server-en.json")]);
      catalog = ui.default as Record<string, string>;
      serverCatalog = server.default as Record<string, string>;
    } catch {
      locale = "de"; // Katalog nicht ladbar → lieber vollständig deutsch als gemischt
    }
  }
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}

/** Sprache wechseln: merken und neu laden (alle Texte, Formate, Tabellen neu) */
export function setLocale(next: Locale, { reload = true } = {}): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* privates Fenster o. Ä. – gilt dann nur bis zum Neuladen über das Profil */
  }
  if (reload && next !== locale) location.reload();
}

/** Profil-Sprache nach dem Login übernehmen (ein Gerät, mehrere Nutzer: das Profil gewinnt) */
export function syncProfileLocale(profile: string | null | undefined): void {
  if (isLocale(profile) && profile !== locale) setLocale(profile);
}

const fill = (s: string, params?: Record<string, string | number>) =>
  params ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : s;

/** Übersetzer für Texte vom Server (server-text.ts meldet sich hier an) – t(x.title) trifft so auch Servertexte */
let serverFallback: ((s: string) => string) | null = null;
export const registerServerFallback = (fn: (s: string) => string) => { serverFallback = fn; };

/** Text übersetzen. Ohne Übersetzung bleibt der deutsche Text stehen (nie leer). */
export function t(source: string | null | undefined, params?: Record<string, string | number>): string {
  if (!source) return source ?? "";
  if (locale === "de") return fill(source, params);
  return fill(catalog[source] || (serverFallback ? serverFallback(source) : source), params);
}

/** Einzahl/Mehrzahl: `tn(n, "{n} Auftrag", "{n} Aufträge")` – n steht als {n} zur Verfügung */
export function tn(n: number, one: string, other: string, params?: Record<string, string | number>): string {
  return t(n === 1 ? one : other, { n, ...params });
}

/** Markiert einen Text zum Übersetzen, ohne ihn schon zu übersetzen (für feste Listen; angezeigt wird mit `t(x)`) */
export const msg = (source: string) => source;

/**
 * Text mit eingebetteten Elementen: `<T text="Mit {kbd} findest du jede Seite" kbd={<kbd>⌘K</kbd>} />`.
 * Platzhalter werden durch die übergebenen Elemente bzw. Werte ersetzt.
 */
export function T({ text, ...values }: { text: string } & Record<string, ReactNode>): ReactNode {
  const parts = t(text).split(/(\{\w+\})/g);
  return createElement(
    Fragment,
    null,
    ...parts.map((p, i) => {
      const k = /^\{(\w+)\}$/.exec(p)?.[1];
      return createElement(Fragment, { key: i }, k && k in values ? values[k] : p);
    }),
  );
}
